import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    Collapse,
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import {
    CaretIcon,
    Checkbox,
    Features,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import FilterEvents from '../events';
import Events from './events/FeatureFilterEvents';

import styles from './FeatureFilter.scss';

const FEATURES_TO_SHOW = 6;

function Feature(props) {
    const {
        featureId,
        isActive,
        onToggle,
    } = props;

    function onChange(state) {
        onToggle(featureId, state);
    }

    return (
        <div
            className={styles.feature}
        >
            <Checkbox
                checked={isActive}
                id={featureId}
                onChange={onChange}
                delayChange
            />
            <Features
                className={styles.carrierFeatures}
                features={[featureId]}
                showTooltips={false}
                showFeatureNameNextToIcon
            />
        </div>
    );
}

Feature.propTypes = {
    featureId: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
};

class FeatureFilter extends React.PureComponent {
    static propTypes = {
        features: PropTypes.arrayOf(
            PropTypes.shape({
                featureId: PropTypes.string.isRequired,
                isActive: PropTypes.bool,
            }),
        ).isRequired,
        isFeaturesFiltered: PropTypes.bool.isRequired,
        isOpenDefault: PropTypes.bool,
        onFeatureReset: PropTypes.func.isRequired,
        onToggleFeature: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
        totalTrips: PropTypes.number.isRequired,
    };

    static defaultProps = {
        isOpenDefault: false,
    };

    constructor(props) {
        super(props);
        const {
            isOpenDefault,
        } = this.props;

        this.state = {
            isOpen: isOpenDefault,
            showNum: FEATURES_TO_SHOW,
        };
    }

    handleToggleMoreButton = () => {
        const { showNum } = this.state;
        const { features } = this.props;
        const totalFeatures = features.length;
        const action = FEATURES_TO_SHOW === showNum
            ? Events.MORE
            : Events.LESS;

        Events.moreButtonClicked(action);

        const eventFunction = action === Events.MORE
            ? FilterEvents.filterListExpanded
            : FilterEvents.filterListCollapsed;

        eventFunction({
            filterType: 'amenities',
        });

        this.setState((oldState) => ({
            showNum: oldState.showNum === totalFeatures
                ? FEATURES_TO_SHOW
                : totalFeatures,
        }));
    };

    handleToggleRow = () => {
        const eventFunction = this.state.isOpen
            ? FilterEvents.filterCollapsed
            : FilterEvents.filterExpanded;

        eventFunction({
            filterType: 'amenities',
        });

        this.setState((currentState) => ({
            isOpen: !currentState.isOpen,
        }));
    };

    handleOnFeatureReset = () => {
        FilterEvents.singleFilterReset({
            filterType: 'amenities',
        });

        this.props.onFeatureReset();
    };

    handleOnToggleFeature = (id, selected) => {
        const { features, totalTrips, onToggleFeature } = this.props;

        let selectedFeatures = features.reduce(
            (featureSet, { featureId, isActive }) => {
                if (isActive) {
                    return [...featureSet, featureId];
                }
                return featureSet;
            },
            [],
        );

        const selectedFeaturesBefore = [...selectedFeatures];

        if (selected) {
            selectedFeatures.push(id);
        } else {
            selectedFeatures = selectedFeatures.filter(
                (feature) => feature !== id,
            );
        }

        onToggleFeature(id, selected);
        Events.featureSelected({
            selectedFeatures: selectedFeatures.sort(),
            selectedFeaturesBefore: selectedFeaturesBefore.sort(),
            totalFeatures: features.length,
            totalTrips,
        });
    };

    render() {
        const {
            features,
            isFeaturesFiltered,
            t,
        } = this.props;
        const {
            isOpen,
            showNum,
        } = this.state;

        const hasTrips = features.length > 0;
        const totalFeatures = features.length;
        const featuresToShow = features
            .sort((featureA, featureB) => {
                const featureStringA = t(
                    `baseApp:${featureA.featureId}_features`,
                );
                const featureStringB = t(
                    `baseApp:${featureB.featureId}_features`,
                );
                return featureStringA.localeCompare(featureStringB);
            })
            .slice(0, showNum);

        return (
            <div
                className={classNames(styles.featureContainer, {
                    [styles.isOpen]: isOpen,
                })}
            >
                <span
                    className={classNames(styles.toggleRow, {
                        [styles.isOpen]: isOpen,
                    })}
                    onClick={this.handleToggleRow}
                    role="button"
                >
                    <span className={styles.title}>{t('amenities')}</span>

                    <CaretIcon showUpArrow={isOpen && hasTrips} />
                </span>
                <Collapse
                    className={styles.collapse}
                    duration={500}
                    isOpen={isOpen && hasTrips}
                >
                    <ul className={styles.featureList}>
                        {featuresToShow.map(({ featureId, isActive }) => {
                            return (
                                <Feature
                                    featureId={featureId}
                                    isActive={isActive}
                                    key={featureId}
                                    onToggle={this.handleOnToggleFeature}
                                />
                            );
                        })}
                    </ul>
                    {hasTrips && (
                        <div className={styles.resetButtonContainer}>
                            {totalFeatures > FEATURES_TO_SHOW && (
                                <span
                                    className={styles.more}
                                    onClick={this.handleToggleMoreButton}
                                    role="button"
                                >
                                    {totalFeatures > featuresToShow.length
                                        ? t('more')
                                        : t('less')}
                                </span>
                            )}
                            <FadeInOutContainer
                                animateHeight={false}
                                duration={250}
                            >
                                <FadeInOut show={isFeaturesFiltered}>
                                    <span
                                        className={styles.resetButton}
                                        onClick={this.handleOnFeatureReset}
                                        role="button"
                                    >
                                        {t('reset')}
                                    </span>
                                </FadeInOut>
                            </FadeInOutContainer>
                        </div>
                    )}
                </Collapse>
            </div>
        );
    }
}

export { Feature, FeatureFilter };
export default localize(FeatureFilter, 'baseApp');
